import { mapGetters } from 'vuex';
import _flatten from 'lodash/flatten';
import _compact from 'lodash/compact';
import _reverse from 'lodash/reverse';
import _sortBy from 'lodash/sortBy';
import form from '@/modules/formHelper';
import { toBoolean, urlFriendly, getReadableFileType } from '@/modules/dataHelper';
import { apiPost, apiStore } from '@/modules/apiHelper';
import { maxFileSize } from '../modules/misc';

export default {
  name: 'ResourceMixin',
  props: ['id'],
  data() {
    return {
      shareTo: _compact([this.$route.query.to || this.id]),
      user: {},
      selectedResources: [],
      myResources: [],
      otherResources: [],
      upload: {
        file: undefined,
        name: undefined,
      },
      uploadErrors: {
        file: undefined,
        name: undefined,
      },
      uploadResource: true,
      uploadButtonText: 'upload',
    };
  },
  watch: {
    // eslint-disable-next-line func-names
    'upload.file': function (val) {
      if (!val) {
        this.upload.file = undefined;
        this.upload.name = undefined;
        this.uploadErrors.file = 'File required';
        this.uploadErrors.name = 'File name required';
        return false;
      }
      this.uploadErrors.file = undefined;
      this.uploadErrors.name = undefined;

      if (val.size > maxFileSize) {
        this.upload.file = undefined;
        this.upload.name = undefined;

        this.$toasted.info('Please upload a file 8MB maximum', {
          duration: 4000,
          position: 'top-right',
        });

        if (this.$refs['resource-file-input']) {
          console.log(this.$refs['resource-file-input']);
          const uploadFileInput = this.$refs['resource-file-input'].$el.querySelector('input');
          uploadFileInput.value = null;
        }
        this.clearFileNameInput();

        return false;
      }

      if (!form.isValidFile(val) && val) {
        this.uploadErrors.file = 'Invalid file type';
        this.upload.file = undefined;
        this.upload.name = undefined;
        this.clearFileNameInput();
        return false;
      }
      this.upload.name = val.name.split('.').slice(0, -1).join('');

      this.clearFileNameInput(this.upload.name || '');
      return true;
    },
    // eslint-disable-next-line func-names
    'upload.name': function (val) {
      if (!form.isValidTextInput(val)) {
        this.uploadErrors.name = 'File name required';
        return;
      }

      this.uploadErrors.name = undefined;
    },
  },
  computed: {
    ...mapGetters([
      'userId',
    ]),
    shareToWho() {
      if (!this.shareTo[0]) {
        return undefined;
      }
      if (this.user.user) {
        return `to ${this.user.user.name}`;
      }
      return undefined;
    },
    totalSelectedResources() {
      return this.selectedResources.length;
    },
    resourcesAreSelected() {
      return this.selectedResources.length > 1;
    },
    myTotalResources() {
      return this.myResources.length;
    },
    canUploadResource() {
      if (
        form.isValidFile(this.upload.file)
        && form.isValidTextInput(this.upload.name)
        && this.upload.file.size < maxFileSize
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    clearFileInput() {
      if (this.$refs['resource-file-input']) {
        const uploadFileInput = this.$refs['resource-file-input'].$el.querySelector('input');
        uploadFileInput.value = null;
      }
    },
    clearFileNameInput(val = '') {
      if (this.$refs['resource-name']) {
        const uploadNameInput = this.$refs['resource-name'].$el.querySelector('input');
        uploadNameInput.value = val;
        uploadNameInput.dispatchEvent(new Event('input'));
      }
    },
    // eslint-disable-next-line consistent-return
    async shareResource(resources, ev, to = []) {
      if (resources.length === 0) {
        this.$toasted.info('Select atleast one resource', {
          duration: 4000,
          position: 'top-right',
        });
        return false;
      }

      // if selected resources are meant for creating resources
      // save to apiPost, then redirect
      if (this.toShareToTask) {
        await apiStore.setItem(`CreateTaskRefResources/${this.id}`, _flatten([resources]));
        this.$router.push({
          name: 'mentorMenteeCreateTask',
          params: {
            id: this.id,
          },
        });

        this.$toasted.info('Resources selected', {
          duration: 2500,
          position: 'top-right',
        });

        await this.$nextTick();

        return true;
      }


      const usersToShareTo = this.shareTo.length > 0 ? this.shareTo : _compact(to);
      const chatRouteName = this.isMentee ? 'mentorChat' : 'mentorMenteeChat';

      if (usersToShareTo.length === 0) {
        await this.openPopOver(ev);
        await this.$nextTick();
        this.selectResource(resources);
        return false;
      }


      this.$toasted.info('Sharing...', {
        duration: 4000,
        position: 'top-right',
      });

      this.$store.commit('openPopOver', [false]);

      // send multiple resources directly to multiple users
      const promises = [];
      usersToShareTo.forEach((userId) => {
        const request = this.$store.dispatch('shareResources', [userId, _flatten([resources])]);
        promises.push(request);
      });

      Promise.all(promises)
        .catch((err) => {
          console.warn('shareResources[] err: ', err);
          this.$toasted.global.appError();
          return false;
        })
        .then((res) => {
          if (!res) return false;
          if (usersToShareTo.length === 1) {
            this.$router.push({ name: chatRouteName, params: { id: usersToShareTo[0] } });
          } else {
            this.$toasted.success('Resources sent!', {
              duration: 5000,
              position: 'top-right',
            });

            this.unselectResource();
            this.selectedMentors = [];
            this.selectedMentees = [];
          }

          return true;
        });
    },
    hasResources(dataName, childDataName = undefined) {
      return childDataName
        ? (this[dataName][childDataName] || []).length > 0
        : this[dataName].length > 0;
    },
    resourceIsSelected(id) {
      return !!this.selectedResources.find(t => t.id === id);
    },
    selectResource(resource) {
      if (Array.isArray(resource)) {
        resource.forEach((resc) => {
          if (!this.resourceIsSelected(resc.id)) {
            this.selectResource(resc);
          }
        });
      } else if (this.resourceIsSelected(resource.id)) {
        this.unselectResource(resource.id);
      } else {
        this.selectedResources.push(resource);
      }
    },
    unselectResource(id) {
      if (!id) {
        this.selectedResources = [];
        return true;
      }

      const index = this.selectedResources.findIndex(t => t.id === id);
      if (index >= 0) {
        this.selectedResources.splice(index, 1);
      }
      return true;
    },
    fileChange(val) {
      [this.upload.file] = val;
    },
    async resetViewData() {
      // this.$refs['resource-file-input'].value = '';
      await this.$nextTick();
      this.upload.file = undefined;
      this.upload.name = undefined;
      await this.$nextTick();
      this.uploadErrors.file = undefined;
      this.uploadErrors.name = undefined;
    },
    async doUploadResource() {
      await this.$nextTick();

      if (!this.canUploadResource || !this.uploadResource) {
        return;
      }
      const formData = new FormData();
      formData.set('name', this.upload.name);
      formData.set('userid', this.userId);
      formData.append('file1', this.upload.file, urlFriendly(this.upload.file.name));

      this.uploadButtonText = 'uploading...';
      this.uploadResource = false;

      const uploader = apiPost(
        'add_resource',
        formData,
        3,
        {
          'Content-Type': 'multipart/form-data',
        },
        (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          this.uploadButtonText = `uploading... (${percentCompleted}%)`;
        },
      )
        .catch((err) => {
          console.warn('Error in uploading resource', err);
          this.$toasted.global.appError();
        })
        .then((res) => {
          this.uploadButtonText = 'upload';
          this.uploadResource = true;

          if (!res) return false;
          if (toBoolean(res.data.error)) {
            this.$toasted.global.appError({
              errorMessage: 'Error in uploading resource',
            });
            return false;
          }

          const message = res.data.message || `Success in uploading ${this.upload.name}`;
          this.$toasted.show(message, {
            type: 'success',
            position: 'bottom-right',
            fullWidth: true,
            duration: 5000,
          });

          this.resetViewData();
          this.clearFileInput();
          this.clearFileNameInput();
          this.$store.commit('openModal', [false]);
          this.getMyResources();
          return true;
        });
      console.log('uploading...', uploader);
    },
    async openUploadModal() {
      this.$store.commit('openModal', [true]);
    },
    async getMyResources() {
      this.loading.myResources = true;
      const resourcesError = 'Error in getting your resources';
      const request = 'user_resources';
      const formData = new FormData();
      formData.set('userid', this.userId);

      apiPost(request, formData, 1)
        .catch((err) => {
          this.error.myResources = resourcesError;
          console.warn(this.error.myResources, err);
          this.$toasted.global.appError({
            errorMessage: this.error.myResources,
          });
        })
        .then((res) => {
          this.loading.myResources = false;
          if (!res) return;

          if (toBoolean(res.data.error)) {
            this.error.myResources = resourcesError;
            this.$toasted.global.appError({
              errorMessage: this.error.myResources,
            });
            return;
          }

          this.myResources = _reverse(_sortBy(res.data[request] || [], 'dateOfPost'));
        });
    },
    async getAdminResources() {
      this.loading.otherResources = true;
      const resourcesError = 'Error in getting resources shared by eMentored';
      const request = 'company_resource_users';
      const formData = new FormData();
      formData.set('userid', this.$store.state.User.id);

      apiPost(request, formData, 9)
        .catch((err) => {
          this.error.otherResources = resourcesError;
          console.warn(this.error.otherResources, err);
          this.$toasted.global.appError({
            errorMessage: this.error.otherResources,
          });
        })
        .then((res) => {
          this.loading.otherResources = false;
          if (!res) return;

          if (toBoolean(res.data.error)) {
            this.error.otherResources = resourcesError;
            this.$toasted.global.appError({
              errorMessage: this.error.otherResources,
            });
            return;
          }

          // console.assert(res.data[request].every(v => v.companyid === this.$store.state.User.id), 'not same company id');

          this.otherResources = _reverse(_sortBy(res.data[request] || [], 'dateOfPost'));

          this.otherResources.forEach((resource, index) => {
            this.otherResources[index] = this.resourceCardData(resource);
          });
        });
    },
    resourceCardData(resource = {}) {
      const fileType = getReadableFileType(resource.resource || resource.path, true);
      const resourceName = ((resource.resource || resource.path).split('/').pop()).split('.')[0];

      return {
        ...resource,
        ...{
          id: resource.resourceid,
          name: resourceName,
          file_type: fileType,
          data: resource.resource || resource.path,
        },
      };
    },
    async deleteResource(resource) {
      if (Number(resource.userid) !== this.userId) return;
      // eslint-disable-next-line no-alert
      const confirm = window.confirm(`Are you sure you want to delete "${resource.name}"?`);
      if (!confirm) return;

      const formData = new FormData();
      formData.set('resourceid', resource.id);

      await apiPost('del_resource', formData, 1)
        .catch(() => {
          this.$toasted.appError();
          return false;
        })
        .then((res) => {
          if (!res) return;
          if (toBoolean(res.data.error)) {
            this.$toasted.appError({ errorMessage: 'Sorry we can\'t delete resource at the moment' });
          }

          this.$toasted.success('Resource deleted', { duration: 4000 });
          this.getMyResources();
        });
    },
  },
  async mounted() {
    // should be false if query is not found
    if (toBoolean(this.$route.query['prompt-upload'])) {
      await this.openUploadModal();
    }

    this.getMyResources();
    this.getAdminResources();

    if (this.shareTo.length > 0) {
      const user = await this.$store.dispatch('getUserData', this.shareTo[0]) || {};
      this.user = Object.assign({}, user);

      this.$toasted.info('You can send more than one resource', {
        duration: 4000,
        position: 'top-right',
      });
    }
  },
};
